/**
 * SongAI Flow video editor: one master song track + stanza scenes on the song timeline.
 */

export function isSongBasedNarrationProject(project) {
  const app = String(project?.app || '').toLowerCase();
  const kind = String(project?.input?.project_kind || '').toLowerCase();
  return app === 'song_based' || kind === 'song_based';
}

export function isSongBasedEditorScenes(scenes) {
  return Boolean((scenes || []).some((s) => s?.song_based_segment));
}

export function songMasterAudioUrl(project, segments) {
  const fromInput = String(project?.input?.song_url || project?.input?.song_audio_url || '').trim();
  if (fromInput) return fromInput;
  const seg = (segments || []).find((s) => s?.audio_url);
  return String(seg?.audio_url || '').trim();
}

/** Stanza window on the master song clock (seconds). */
export function songSegmentWindow(segment) {
  const startRaw = segment?.timeline_start ?? segment?.statement_start_time ?? segment?.statement_start_time_local;
  const endRaw = segment?.timeline_end ?? segment?.statement_end_time ?? segment?.statement_end_time_local;
  const start = Number(startRaw);
  const end = Number(endRaw);
  const dur = Number(segment?.audio_duration ?? segment?.duration);

  if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
    return { start, end, duration: end - start };
  }
  if (Number.isFinite(start) && dur > 0) {
    return { start, end: start + dur, duration: dur };
  }
  return { start: 0, end: dur > 0 ? dur : 5, duration: dur > 0 ? dur : 5 };
}

/** True when a stanza has WhisperX / global-align timing on the master song clock. */
export function songSceneHasAlignTiming(scene) {
  if (!scene) return false;
  const w = songSegmentWindow(scene);
  if (!(w.duration > 0)) return false;
  if (Array.isArray(scene.word_timestamps) && scene.word_timestamps.length > 0) return true;
  const start = scene.statement_start_time ?? scene.statement_start_time_local ?? scene.timeline_start;
  const end = scene.statement_end_time ?? scene.statement_end_time_local ?? scene.timeline_end;
  return start != null && end != null && Number(end) > Number(start);
}

export function songTimelineDuration(project, segments) {
  const total = Number(project?.total_duration) || 0;
  const maxEnd = (segments || []).reduce((max, seg) => {
    const w = songSegmentWindow(seg);
    return Math.max(max, w.end);
  }, 0);
  return Math.max(total, maxEnd, 1);
}

/**
 * One continuous audio lane clip for the uploaded song (not per-stanza duplicates).
 * `resolveUrl` should be mediaUrlForPlayback from the editor.
 */
export function buildSongMasterAudioLane(project, segments, resolveUrl, narrationProjectId = null) {
  const rawUrl = songMasterAudioUrl(project, segments);
  if (!rawUrl) return [];

  const url = typeof resolveUrl === 'function' ? resolveUrl(rawUrl) : rawUrl;
  const duration = songTimelineDuration(project, segments);
  const firstId = (segments || []).find((s) => s?.id)?.id || 'song';

  const clip = {
    id: 'song-master-audio',
    name: 'Original song',
    sceneId: firstId,
    url,
    startTime: 0,
    duration,
    sourceDuration: duration,
    trimStart: 0,
    volume: 0.8,
    muted: false,
    sourceType: 'song_master',
  };

  if (narrationProjectId) {
    clip.waveformUrl = `/narration/projects/${narrationProjectId}/segments/${firstId}/waveform`;
  }

  return [clip];
}

export function songStanzaSubtitleText(scene) {
  const authored = String(scene?.authored_script || scene?.song_lyric_stanza || '').trim();
  if (authored) return authored;
  return String(scene?.narration_text || '').trim();
}

/** Preserve editor overrides when rebuilding subtitle cues from scenes/audio. */
export function subtitleCueEditorOverlay(cue) {
  if (!cue) return null;
  return {
    text: cue.text,
    muted: cue.muted,
    manualEdit: cue.manualEdit,
    manualTiming: cue.manualTiming,
    startTime: cue.startTime,
    duration: cue.duration,
  };
}

/** Apply user-dragged subtitle timing when manualTiming is set. */
export function applySubtitleCueOverlayTiming(defaults, overlay) {
  const startTime = Number(defaults.startTime) || 0;
  const duration = Math.max(0.01, Number(defaults.duration) || 0.01);
  if (!overlay?.manualTiming) {
    return { startTime, duration, manualTiming: false };
  }
  return {
    startTime: Number.isFinite(Number(overlay.startTime)) ? Number(overlay.startTime) : startTime,
    duration: Math.max(0.01, Number(overlay.duration) || duration),
    manualTiming: true,
  };
}

/** Word timings stored on the segment in global song seconds → clip-local seconds. */
export function songClipLocalWordTimestamps(segment, window) {
  const words = Array.isArray(segment?.word_timestamps) ? segment.word_timestamps : [];
  const offset = Number(window?.start) || 0;
  const span = Math.max(0.01, Number(window?.duration) || 0.01);
  if (!words.length) return [];
  return words.map((w) => {
    const start = Math.max(0, (Number(w?.start) || 0) - offset);
    const end = Math.max(start + 0.02, (Number(w?.end) || start + 0.05) - offset);
    return {
      ...w,
      word: w?.word != null ? String(w.word) : '',
      start: Math.round(Math.min(span, start) * 1000) / 1000,
      end: Math.round(Math.min(span, end) * 1000) / 1000,
    };
  });
}

/**
 * One subtitle cue per aligned stanza on the master song clock (not one cue for the whole song).
 */
export function buildSongStanzaSubtitleTracks(scenes, videoTracks, prevSubtitleTracks = [[]]) {
  const sorted = [...(scenes || [])]
    .filter((s) => s?.id)
    .sort(
      (a, b) => (Number(a.scene_number ?? a.segment_number) || 0) - (Number(b.scene_number ?? b.segment_number) || 0),
    );
  const videoLane0 = (videoTracks || [])[0] || [];
  const prev = (prevSubtitleTracks || [[]])[0] || [];
  const overlayById = Object.fromEntries(
    prev
      .filter((c) => c?.id)
      .map((c) => [String(c.id), subtitleCueEditorOverlay(c)]),
  );
  const overlayByScene = Object.fromEntries(
    prev
      .filter((c) => c?.sceneId)
      .map((c) => [String(c.sceneId), subtitleCueEditorOverlay(c)]),
  );

  const cues = sorted
    .map((scene) => {
      const sid = String(scene.id);
      const window = songSegmentWindow(scene);
      const videoClip = videoLane0.find(
        (vc) => String(vc.sceneId || vc.id) === sid,
      );
      const cueId = `subtitle-song-stanza-${sid}`;
      const o = overlayById[cueId] || overlayByScene[sid];
      const hasOverlay = Boolean(o);
      const script = songStanzaSubtitleText(scene);
      const text = hasOverlay && o.manualEdit && String(o.text || '').trim()
        ? String(o.text).trim()
        : script;
      if (!text) return null;

      const defaultTiming = {
        startTime: window.start,
        duration: Math.max(0.01, window.duration),
      };
      const timing = applySubtitleCueOverlayTiming(defaultTiming, o);

      return {
        id: cueId,
        audioClipId: 'song-master-audio',
        sceneId: sid,
        videoClipId: videoClip?.id,
        startTime: timing.startTime,
        duration: timing.duration,
        text,
        muted: hasOverlay ? Boolean(o.muted) : false,
        manualEdit: Boolean(hasOverlay && o.manualEdit),
        manualTiming: timing.manualTiming,
      };
    })
    .filter(Boolean);

  return [cues, ...(prevSubtitleTracks || []).slice(1)];
}

export function buildSongMasterAudioLaneFromScenes(scenes, resolveUrl, narrationProjectId = null, totalDuration = 0) {
  const sorted = [...(scenes || [])].filter((s) => s?.id).sort(
    (a, b) => (Number(a.scene_number ?? a.segment_number) || 0) - (Number(b.scene_number ?? b.segment_number) || 0),
  );
  if (!sorted.length) return [];

  const rawUrl = String(
    sorted.find((s) => s.song_master_audio_url)?.song_master_audio_url
      || sorted.find((s) => s.audio_url)?.audio_url
      || '',
  ).trim();
  if (!rawUrl) return [];

  const url = typeof resolveUrl === 'function' ? resolveUrl(rawUrl) : rawUrl;
  const duration = Math.max(
    Number(totalDuration) || 0,
    sorted.reduce((max, seg) => Math.max(max, songSegmentWindow(seg).end), 0),
    1,
  );
  const firstId = sorted[0].id;

  const clip = {
    id: 'song-master-audio',
    name: 'Original song',
    sceneId: firstId,
    url,
    startTime: 0,
    duration,
    sourceDuration: duration,
    trimStart: 0,
    volume: 0.8,
    muted: false,
    sourceType: 'song_master',
  };

  if (narrationProjectId) {
    clip.waveformUrl = `/narration/projects/${narrationProjectId}/segments/${firstId}/waveform`;
  }

  return [clip];
}

/** Alternate camera angles generated for a stanza (excludes the master image_url). */
export function sceneMultishotImages(scene) {
  const raw = scene?.multishot_images || scene?.multishotImages || [];
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((row) => row && String(row.url || '').trim())
    .slice()
    .sort((a, b) => (Number(a.index) || 0) - (Number(b.index) || 0));
}

export function multishotShotEnabled(shot) {
  return shot?.enabled !== false;
}

export function sceneMultishotMasterEnabled(scene) {
  return scene?.multishot_include_master !== false;
}

/** Multishots marked enabled for timeline (does not include master). */
export function enabledMultishotImages(scene) {
  return sceneMultishotImages(scene).filter(multishotShotEnabled);
}

/**
 * Timeline parts for a stanza: enabled master (optional) + enabled multishots only.
 * Each part: { partIndex, imageUrl, isMaster, shotIndex }
 */
export function enabledMultishotTimelineParts(scene) {
  const parts = [];
  const masterUrl = String(scene?.image_url || scene?.imageUrl || '').trim();
  if (sceneMultishotMasterEnabled(scene) && masterUrl) {
    parts.push({
      partIndex: parts.length,
      imageUrl: masterUrl,
      isMaster: true,
      shotIndex: null,
    });
  }
  enabledMultishotImages(scene).forEach((shot) => {
    parts.push({
      partIndex: parts.length,
      imageUrl: shot.url,
      isMaster: false,
      shotIndex: Number(shot.index) || parts.length,
    });
  });
  return parts;
}

/**
 * Split one song stanza into equal-duration clips for enabled parts only.
 * `makeClip(scene, startTime, duration, sceneColorIndex, opts)` returns a timeline clip object.
 */
export function expandSongSceneToTimelineClips(scene, sceneColorIndex, makeClip) {
  const window = songSegmentWindow(scene);
  const blockDur = Math.max(0.01, window.duration);
  const parts = enabledMultishotTimelineParts(scene);
  const hasAnyMultishot = sceneMultishotImages(scene).length > 0;

  if (!hasAnyMultishot) {
    return [makeClip(scene, window.start, blockDur, sceneColorIndex, null)];
  }

  if (!parts.length) {
    return [];
  }

  if (parts.length === 1) {
    const only = parts[0];
    // Single enabled master should behave like a normal scene clip (supports extend-to-audio).
    if (only.isMaster) {
      return [makeClip(scene, window.start, blockDur, sceneColorIndex, {
        imageUrl: only.imageUrl,
        nameSuffix: '',
      })];
    }
    return [makeClip(scene, window.start, blockDur, sceneColorIndex, {
      clipId: `${scene.id}__ms${only.partIndex}`,
      parentSceneId: scene.id,
      multishotPart: only.partIndex,
      multishotShotIndex: only.shotIndex,
      multishotIsMaster: only.isMaster,
      multishotSegmentDuration: blockDur,
      imageUrl: only.imageUrl,
      nameSuffix: '',
    })];
  }

  const sliceDur = blockDur / parts.length;
  const totalParts = parts.length;

  return parts.map((part, idx) => {
    const start = window.start + idx * sliceDur;
    return makeClip(scene, start, sliceDur, sceneColorIndex, {
      clipId: `${scene.id}__ms${part.partIndex}`,
      parentSceneId: scene.id,
      multishotPart: part.partIndex,
      multishotShotIndex: part.shotIndex,
      multishotIsMaster: part.isMaster,
      multishotSegmentDuration: sliceDur,
      imageUrl: part.imageUrl,
      nameSuffix: ` (${idx + 1}/${totalParts})`,
    });
  });
}

/** Preserve editor tweaks when re-snapping song clips to aligned stanza windows. */
export function mergeSongMagicEditClipPresets(existingClip, freshClip) {
  if (!existingClip || !freshClip) return freshClip;
  return {
    ...freshClip,
    motionPreset: existingClip.motionPreset ?? freshClip.motionPreset,
    motionStrength: existingClip.motionStrength ?? freshClip.motionStrength,
    audioMotionTimeline: existingClip.audioMotionTimeline ?? freshClip.audioMotionTimeline,
    transitionOut: existingClip.transitionOut ?? freshClip.transitionOut,
    transitionDuration: existingClip.transitionDuration ?? freshClip.transitionDuration,
    filters: existingClip.filters ?? freshClip.filters,
  };
}

const MIN_MULTISHOT_SLICE_SEC = 0.05;

function normalizeMultishotSceneId(sceneId) {
  return String(sceneId || '').replace(/^audio-/, '').replace(/^seg-audio-/, '').split('-seg-')[0];
}

/** True for per-shot timeline slices (master + multishot parts). */
export function isMultishotTimelineClip(clip) {
  return clip?.multishotPart != null && (
    clip?.parentSceneId != null || String(clip?.id || '').includes('__ms')
  );
}

/**
 * Audio-only span for multishot equal split (segment timeline or scene mix).
 */
export function sceneMultishotAudioSpanSeconds(scene) {
  let segEnd = 0;
  let hasSeg = false;
  (Array.isArray(scene?.audio_timeline) ? scene.audio_timeline : []).forEach((t) => {
    if (!t?.segment_audio_url) return;
    hasSeg = true;
    segEnd = Math.max(segEnd, (Number(t.start_seconds) || 0) + (Number(t.duration_seconds) || 0));
  });
  const mix = Number(
    scene?.audio_duration_seconds ?? scene?.audio_duration ?? scene?.duration_seconds ?? scene?.duration,
  );
  const mixDur = Number.isFinite(mix) && mix > 0 ? mix : 0;
  if (hasSeg) return Math.max(MIN_MULTISHOT_SLICE_SEC, segEnd, mixDur);
  if (mixDur > 0) return Math.max(MIN_MULTISHOT_SLICE_SEC, mixDur);
  return MIN_MULTISHOT_SLICE_SEC;
}

/**
 * Split scene narration audio into one clip per multishot video slice (equal source trim windows).
 */
export function expandAudioLaneForMultishot(videoClips, audioClips) {
  const partsByScene = {};
  (videoClips || []).forEach((vc) => {
    if (vc?.multishotPart == null) return;
    const sid = normalizeMultishotSceneId(vc.parentSceneId ?? vc.sceneId);
    if (!sid) return;
    if (!partsByScene[sid]) partsByScene[sid] = [];
    partsByScene[sid].push(vc);
  });
  Object.values(partsByScene).forEach((arr) => {
    arr.sort((a, b) => (Number(a.multishotPart) || 0) - (Number(b.multishotPart) || 0));
  });

  const audioByScene = {};
  (audioClips || []).forEach((ac) => {
    const sid = normalizeMultishotSceneId(ac.sceneId);
    if (!sid) return;
    if (!audioByScene[sid]) audioByScene[sid] = [];
    audioByScene[sid].push(ac);
  });

  const out = [];
  const allSceneIds = new Set([...Object.keys(partsByScene), ...Object.keys(audioByScene)]);

  for (const sid of allSceneIds) {
    const parts = partsByScene[sid];
    const sceneAudio = (audioByScene[sid] || []).sort(
      (a, b) => (Number(a.startTime) || 0) - (Number(b.startTime) || 0),
    );
    if (!sceneAudio.length) continue;

    if (!parts || parts.length <= 1) {
      const p0 = parts?.[0];
      sceneAudio.forEach((a) => {
        out.push(p0 ? { ...a, pairedVideoClipId: p0.id } : a);
      });
      continue;
    }

    const primary = sceneAudio[0];
    const srcTotal = Number(primary.sourceDuration) || Number(primary.duration) || 0;
    const perPartSrc = srcTotal > 0 ? srcTotal / parts.length : 0;

    parts.forEach((vc, p) => {
      const partIdx = Number(vc.multishotPart) ?? p;
      const sliceDur = Number(vc.multishotSegmentDuration)
        || (Number(primary.duration) / parts.length)
        || perPartSrc
        || MIN_MULTISHOT_SLICE_SEC;
      const sliceStart = Number(vc.startTime) || 0;
      const srcSliceStart = perPartSrc > 0 ? perPartSrc * partIdx : sliceDur * partIdx;

      out.push({
        ...primary,
        id: `multishot-audio-${sid}-ms${partIdx}`,
        pairedVideoClipId: vc.id,
        parentSceneId: sid,
        multishotPart: partIdx,
        multishotSegmentDuration: sliceDur,
        startTime: sliceStart,
        duration: sliceDur,
        sourceDuration: srcTotal || sliceDur * parts.length,
        audioSourceTrimStart: srcSliceStart,
        sourceType: primary.sourceType || 'scene_segment',
      });
    });
  }

  return out.sort((a, b) => (Number(a.startTime) || 0) - (Number(b.startTime) || 0));
}

function timelineClipMergeKey(c) {
  if (c?.parentSceneId != null) {
    return `${c.parentSceneId}::ms${c.multishotPart ?? 0}`;
  }
  return String(c.sceneId || c.id || '');
}

function mergeRebuiltTimelineVideoClip(old, fresh) {
  if (!old || !fresh) return fresh;
  const videoUrl = fresh.videoUrl || old.videoUrl || null;
  const merged = mergeSongMagicEditClipPresets(old, { ...fresh, videoUrl });
  if (videoUrl && old.videoUrl && !fresh.videoUrl) {
    return {
      ...merged,
      trimStart: old.trimStart ?? merged.trimStart,
      trimEnd: old.trimEnd ?? merged.trimEnd,
      videoSourceDurationMax: old.videoSourceDurationMax || merged.videoSourceDurationMax,
      videoMediaDurationSeconds: old.videoMediaDurationSeconds ?? merged.videoMediaDurationSeconds,
      playbackDuration: old.playbackDuration ?? merged.playbackDuration,
    };
  }
  return merged;
}

export function mergeSongMagicEditVideoTracks(prevVideoTracks, nextVideoTracks) {
  const prevFlat = (prevVideoTracks || []).flat().filter(Boolean);
  const byId = Object.fromEntries(prevFlat.filter((c) => c?.id).map((c) => [String(c.id), c]));
  const bySceneKey = Object.fromEntries(
    prevFlat.filter((c) => timelineClipMergeKey(c)).map((c) => [timelineClipMergeKey(c), c]),
  );

  return (nextVideoTracks || []).map((track) =>
    (track || []).map((fresh) => {
      const old = byId[String(fresh.id)] || bySceneKey[timelineClipMergeKey(fresh)];
      return old ? mergeSongMagicEditClipPresets(old, fresh) : fresh;
    }),
  );
}

/** Keep generated video URLs when narration multishot timeline is rebuilt. */
export function mergeRebuiltNarrationVideoTracks(prevVideoTracks, nextVideoTracks) {
  const prevFlat = (prevVideoTracks || []).flat().filter(Boolean);
  const byId = Object.fromEntries(prevFlat.filter((c) => c?.id).map((c) => [String(c.id), c]));
  const bySceneKey = Object.fromEntries(
    prevFlat.filter((c) => timelineClipMergeKey(c)).map((c) => [timelineClipMergeKey(c), c]),
  );

  return (nextVideoTracks || []).map((track) =>
    (track || []).map((fresh) => {
      const old = byId[String(fresh.id)] || bySceneKey[timelineClipMergeKey(fresh)];
      return old ? mergeRebuiltTimelineVideoClip(old, fresh) : fresh;
    }),
  );
}
